// src/lib/logger.ts
// Structured logger with log-level gating and PII scrubbing.

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface StructuredEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
}

const isDev = import.meta.env.DEV && import.meta.env.MODE !== 'test';
const isTest = import.meta.env.MODE === 'test';

// ---------------------------------------------------------------------------
// PII / secret scrubbing
// ---------------------------------------------------------------------------
const SCRUB_PATTERNS: [RegExp, string][] = [
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]'],
  [/\b(?:Bearer\s+)[A-Za-z0-9._~+/=-]+/gi, 'Bearer [TOKEN_REDACTED]'],
  [/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, '[JWT_REDACTED]'],
  [/\b\d{4}[- ]?\d{4}[- ]?\d{4}[- ]?\d{4}\b/g, '[CARD_REDACTED]'],
  [/\b(sk_live_|sk_test_|pk_live_|pk_test_|whsec_)[A-Za-z0-9]+/g, '[STRIPE_KEY_REDACTED]'],
  [/\bghp_[A-Za-z0-9]{36,}\b/g, '[GITHUB_TOKEN_REDACTED]'],
  [/"?password"?\s*[:=]\s*"[^"]*"/gi, '"password":"[REDACTED]"'],
];

function scrub(value: string): string {
  let result = value;
  for (const [pattern, replacement] of SCRUB_PATTERNS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

function sanitizeArgs(args: unknown[]): unknown[] {
  return args.map((arg) => {
    if (typeof arg === 'string') return scrub(arg);
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.parse(scrub(JSON.stringify(arg)));
      } catch {
        return arg;
      }
    }
    return arg;
  });
}

// ---------------------------------------------------------------------------
// Structured entry builder
// ---------------------------------------------------------------------------
function buildEntry(level: LogLevel, args: unknown[]): StructuredEntry {
  const first = args[0];
  const message = typeof first === 'string' ? first : String(first);
  const rest = args.slice(1);

  const entry: StructuredEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
  };

  if (rest.length === 1 && typeof rest[0] === 'object' && rest[0] !== null) {
    entry.context = rest[0] as Record<string, unknown>;
  } else if (rest.length > 0) {
    entry.context = { extra: rest };
  }

  return entry;
}

// ---------------------------------------------------------------------------
// Console dispatch (structured JSON in prod, pretty in dev)
// ---------------------------------------------------------------------------
const CONSOLE_MAP: Record<LogLevel, (...a: unknown[]) => void> = {
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
};

function emit(level: LogLevel, args: unknown[]): void {
  const safe = sanitizeArgs(args);
  if (isDev) {
    CONSOLE_MAP[level](...safe);
  } else if (!isTest) {
    const entry = buildEntry(level, safe);
    CONSOLE_MAP[level](JSON.stringify(entry));
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const logger = {
  debug: (...args: unknown[]): void => {
    if (isDev) emit('debug', args);
  },
  log: (...args: unknown[]): void => {
    if (isDev) emit('info', args);
  },
  info: (...args: unknown[]): void => {
    if (isDev) emit('info', args);
  },
  warn: (...args: unknown[]): void => {
    emit('warn', args);
  },
  error: (...args: unknown[]): void => {
    emit('error', args);
  },
};

export default logger;
